// 🔒 비밀번호 정책 — 서버(RC Accounts_Password_Policy_*) 규칙을 클라에서 그대로 재현한다.
//   SettingsModal 에만 있던 구현을 공용으로 승격 (2026-08-24) — 로그인 강제변경 화면이 규칙을
//   하드코딩해 안내하던 탓에 실제 정책(8자·대문자 불필요)과 어긋나 사용자가 반복 실패했다.
//   🔴 규칙 문구를 새로 하드코딩하지 말 것 — 항상 loadPasswordPolicy() 결과로 만든다.
import i18n from '../i18n';

export interface PasswordPolicy {
	enabled: boolean;
	minLength: number;
	maxLength: number;
	atLeastOneLowercase: boolean;
	atLeastOneUppercase: boolean;
	atLeastOneNumber: boolean;
	atLeastOneSpecialCharacter: boolean;
	forbidRepeatingCharacters: boolean;
	forbidRepeatingCount: number;
}

export const PASSWORD_POLICY_FALLBACK: PasswordPolicy = {
	enabled: false, minLength: 8, maxLength: -1,
	atLeastOneLowercase: false, atLeastOneUppercase: false,
	atLeastOneNumber: false, atLeastOneSpecialCharacter: false,
	forbidRepeatingCharacters: false, forbidRepeatingCount: 3,
};

// RC `/api/v1/pw.getPolicy` 는 authRequired:false 공개 엔드포인트 — 로그인 전에도 호출 가능.
//   (`/api/v1/settings/Accounts_Password_Policy_*` 는 view-privileged-setting 권한 필요 → 일반 user 403)
//   응답: { enabled, policy: [['get-password-policy-minLength', {minLength:8}], ['...Lowercase'], …] }
//   base 는 데스크톱(다른 오리진)에서 필요 — 생략 시 같은 오리진.
export async function loadPasswordPolicy(authToken?: string | null, userId?: string | null, base = ''): Promise<PasswordPolicy> {
	try {
		const headers: Record<string, string> = {};
		if (authToken && userId) { headers['X-Auth-Token'] = authToken; headers['X-User-Id'] = userId; }
		const r = await fetch(`${base}/api/v1/pw.getPolicy`, { headers });
		const j = (await r.json().catch(() => ({}))) as {
			enabled?: boolean;
			policy?: [string, Record<string, number | boolean>?][];
		};
		if (!r.ok || !j || typeof j.enabled !== 'boolean') return PASSWORD_POLICY_FALLBACK;
		const out: PasswordPolicy = { ...PASSWORD_POLICY_FALLBACK, enabled: j.enabled };
		for (const entry of j.policy ?? []) {
			const [name, params] = entry;
			switch (name) {
				case 'get-password-policy-minLength':
					out.minLength = Number(params?.minLength ?? out.minLength); break;
				case 'get-password-policy-maxLength':
					out.maxLength = Number(params?.maxLength ?? out.maxLength); break;
				case 'get-password-policy-mustContainAtLeastOneLowercase':
					out.atLeastOneLowercase = true; break;
				case 'get-password-policy-mustContainAtLeastOneUppercase':
					out.atLeastOneUppercase = true; break;
				case 'get-password-policy-mustContainAtLeastOneNumber':
					out.atLeastOneNumber = true; break;
				case 'get-password-policy-mustContainAtLeastOneSpecialCharacter':
					out.atLeastOneSpecialCharacter = true; break;
				case 'get-password-policy-forbidRepeatingCharacters':
					out.forbidRepeatingCharacters = true; break;
				case 'get-password-policy-forbidRepeatingCharactersCount':
					out.forbidRepeatingCount = Number(params?.forbidRepeatingCharactersCount ?? out.forbidRepeatingCount); break;
			}
		}
		return out;
	} catch {
		return PASSWORD_POLICY_FALLBACK;
	}
}

// 🔴 특수문자 판정은 RC 와 같은 정의를 쓴다 — RC PasswordPolicy 는 `[^A-Za-z0-9 ]`(영숫자·공백 외 전부).
//   좁은 문자 목록으로 검사하면 서버는 통과시키는 비번을 클라가 막는 어긋남이 생긴다.
const SPECIAL_RE = /[^A-Za-z0-9 ]/;
// 같은 문자 연속 금지 — RC 정규식은 `(.)\1{N,}` (N = forbidRepeatingCount) 이라 "연속 N+1자"부터 걸린다.
//   🔴 종전 SettingsModal 은 `{N-1,}` 을 써서 서버보다 한 글자 엄격했다(서버는 통과시키는 비번을 클라가 막음).
//   안내 문구(`pwHintRepeat`/`pwRepeat`)는 "N회 미만/N회 이상 금지" 형식이라 N+1 을 넣어야 규칙과 일치한다.
const repeatRe = (count: number): RegExp => new RegExp(`(.)\\1{${Math.max(1, count)},}`);
const repeatLabelN = (count: number): number => Math.max(1, count) + 1;

export interface PasswordRule { key: string; label: string; ok: boolean }

// 정책을 만족 여부와 함께 규칙 목록으로 편다 — 입력 중 체크리스트 표시용.
export function passwordRules(pw: string, p: PasswordPolicy): PasswordRule[] {
	if (!p.enabled) return [];
	const rules: PasswordRule[] = [];
	if (p.minLength > 0) rules.push({ key: 'min', label: i18n.t('settings.pwHintMin', { n: p.minLength }), ok: pw.length >= p.minLength });
	if (p.maxLength > 0) rules.push({ key: 'max', label: i18n.t('settings.pwHintMax', { n: p.maxLength }), ok: pw.length <= p.maxLength });
	if (p.atLeastOneLowercase) rules.push({ key: 'lower', label: i18n.t('settings.pwHintLower'), ok: /[a-z]/.test(pw) });
	if (p.atLeastOneUppercase) rules.push({ key: 'upper', label: i18n.t('settings.pwHintUpper'), ok: /[A-Z]/.test(pw) });
	if (p.atLeastOneNumber) rules.push({ key: 'number', label: i18n.t('settings.pwHintNumber'), ok: /\d/.test(pw) });
	if (p.atLeastOneSpecialCharacter) rules.push({ key: 'special', label: i18n.t('settings.pwHintSpecial'), ok: SPECIAL_RE.test(pw) });
	if (p.forbidRepeatingCharacters) {
		rules.push({ key: 'repeat', label: i18n.t('settings.pwHintRepeat', { n: repeatLabelN(p.forbidRepeatingCount) }), ok: !repeatRe(p.forbidRepeatingCount).test(pw) });
	}
	return rules;
}

// 첫 위반 사유 1건 — 기존 SettingsModal 호출부 호환(문자열 or null).
export function validatePassword(pw: string, p: PasswordPolicy): string | null {
	if (!p.enabled) return pw.length >= 1 ? null : i18n.t('settings.pwEnter');
	if (p.minLength > 0 && pw.length < p.minLength) return i18n.t('settings.pwMin', { n: p.minLength });
	if (p.maxLength > 0 && pw.length > p.maxLength) return i18n.t('settings.pwMax', { n: p.maxLength });
	if (p.atLeastOneLowercase && !/[a-z]/.test(pw)) return i18n.t('settings.pwLower');
	if (p.atLeastOneUppercase && !/[A-Z]/.test(pw)) return i18n.t('settings.pwUpper');
	if (p.atLeastOneNumber && !/\d/.test(pw)) return i18n.t('settings.pwNumber');
	if (p.atLeastOneSpecialCharacter && !SPECIAL_RE.test(pw)) return i18n.t('settings.pwSpecial');
	if (p.forbidRepeatingCharacters && repeatRe(p.forbidRepeatingCount).test(pw)) {
		return i18n.t('settings.pwRepeat', { n: repeatLabelN(p.forbidRepeatingCount) });
	}
	return null;
}
